import type { Foto, FotoSoort, Registratie } from '@/types/domain';
import { getConfig } from '@/config/appConfig';
import { nieuweId } from '@/utils/id';
import { ServiceFout, type FotoService } from '../types';
import { getSupabase, leesbareFout } from './supabaseClient';

/**
 * Foto's gaan naar een niet-openbare opslagplek, met per woning een eigen map
 * (mapnaam = BAG-id). In de database staat alleen de metadata.
 *
 * Omdat de opslag niet openbaar is, vraagt de app per foto een tijdelijke
 * link op. Iemand die zo'n link kopieert kan er dus niet eeuwig bij.
 */

const LINK_GELDIGHEID_SECONDEN = 60 * 60;

/** Maakt een bestandsnaam veilig voor gebruik in een opslagpad. */
function veiligeBestandsnaam(naam: string): string {
  // Alles wat geen letter, cijfer, punt, streepje of underscore is wordt een
  // underscore. De echte bestandsnaam blijft bewaard in het veld bestandsnaam.
  return naam.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/^[.]+/, '').slice(0, 100) || 'foto.jpg';
}

function bucket() {
  return getSupabase().storage.from(getConfig().supabase.fotoBucket);
}

export const supabaseFotoService: FotoService = {
  async upload(registratie: Registratie, bestand: File, soort: FotoSoort): Promise<Foto> {
    const map = registratie.bagId || registratie.id;
    const stempel = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
    const pad = `${map}/${soort.toLowerCase()}_${stempel}_${veiligeBestandsnaam(bestand.name)}`;

    const { error } = await bucket().upload(pad, bestand, {
      contentType: bestand.type || 'image/jpeg',
      upsert: false,
    });

    if (error) throw new ServiceFout(leesbareFout(error), error);

    return {
      id: nieuweId('foto'),
      bestandsnaam: bestand.name,
      pad,
      soort,
      toelichting: '',
      grootteBytes: bestand.size,
      toegevoegdOp: new Date().toISOString(),
      lokaal: false,
    };
  },

  async verwijderen(_registratie: Registratie, foto: Foto): Promise<void> {
    if (foto.lokaal) return; // Stond alleen lokaal.
    const { error } = await bucket().remove([foto.pad]);
    if (error) throw new ServiceFout(leesbareFout(error), error);
  },

  async weergaveUrl(foto: Foto): Promise<string> {
    if (foto.dataUrl) return foto.dataUrl;

    const { data, error } = await bucket().createSignedUrl(foto.pad, LINK_GELDIGHEID_SECONDEN);
    if (error || !data?.signedUrl) {
      throw new ServiceFout(leesbareFout(error), error);
    }
    return data.signedUrl;
  },
};
